import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Card, message, Spin, Modal, InputNumber } from 'antd';
import { SaveOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getLesson, updateLesson, createLesson, getCourses, getCourse } from './api';
import MarkdownEditor from './MarkdownEditor';

const { TextArea } = Input;
const { Option } = Select;

interface LessonFormProps {
  lessonId?: number;
  courseId?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface LessonData {
  courseId: number;
  title: string;
  content?: string;
  sort_order: number;
  examType: 'test' | 'open';
}

interface Course {
  id: number;
  title: string;
}

const LessonForm: React.FC<LessonFormProps> = ({ lessonId, courseId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const isEditing = !!lessonId;

  useEffect(() => {
    loadCourses();
    if (lessonId) {
      loadLesson();
    } else if (courseId) {
      // Если создаем урок для конкретного курса, устанавливаем его по умолчанию
      form.setFieldValue('courseId', courseId);
      loadNextSortOrder(courseId);
    }
  }, [lessonId, courseId]);

  const loadCourses = async () => {
    setCoursesLoading(true);
    try {
      const coursesData = await getCourses();
      setCourses(coursesData);
    } catch (error) {
      message.error('Ошибка загрузки курсов');
    } finally {
      setCoursesLoading(false);
    }
  };

  const loadNextSortOrder = async (selectedCourseId: number) => {
    try {
      const course = await getCourse(selectedCourseId);
      const lessons = course.lessons || [];
      const maxSortOrder = lessons.length > 0 ? Math.max(...lessons.map((l: any) => l.sort_order)) : 0;
      form.setFieldValue('sort_order', maxSortOrder + 1);
    } catch (error) {
      console.error('Ошибка загрузки следующего порядкового номера:', error);
    }
  };

  const loadLesson = async () => {
    if (!lessonId) return;
    
    setInitialLoading(true);
    try {
      const lesson = await getLesson(lessonId);
      form.setFieldsValue({
        courseId: lesson.courseId,
        title: lesson.title,
        content: lesson.content || '',
        sort_order: lesson.sort_order,
        examType: lesson.examType || 'test', // Устанавливаем значение по умолчанию
      });
      setHasChanges(false);
    } catch (error) {
      message.error('Ошибка загрузки урока');
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values: LessonData) => {
    setLoading(true);
    try {
      if (isEditing && lessonId) {
        await updateLesson(lessonId, values);
        message.success('Урок успешно обновлен');
      } else {
        await createLesson(values);
        message.success('Урок успешно создан');
      }
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error(error.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Подтверждение',
        content: 'У вас есть несохраненные изменения. Вы уверены, что хотите выйти?',
        okText: 'Да, выйти',
        cancelText: 'Отмена',
        onOk: () => {
          setHasChanges(false);
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  const handleValuesChange = (changedValues: any) => {
    setHasChanges(true);
    
    // Если изменился курс, обновляем порядковый номер
    if (changedValues.courseId && !isEditing) {
      loadNextSortOrder(changedValues.courseId);
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ padding: 0, height: 'auto' }}
          />
          {isEditing ? 'Редактировать урок' : 'Создать новый урок'}
        </div>
      }
      style={{ maxWidth: 800, margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        initialValues={{
          sort_order: 1,
          examType: 'test', // Значение по умолчанию
        }}
      >
        <Form.Item
          name="courseId"
          label="Курс"
          rules={[{ required: true, message: 'Пожалуйста, выберите курс' }]}
        >
          <Select 
            placeholder="Выберите курс"
            loading={coursesLoading}
            disabled={!!courseId} // Если courseId передан, блокируем выбор
          >
            {courses.map(course => (
              <Option key={course.id} value={course.id}>
                {course.title}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="title"
          label="Название урока"
          rules={[{ required: true, message: 'Пожалуйста, введите название урока' }]}
        >
          <Input placeholder="Введите название урока" />
        </Form.Item>

        <Form.Item
          name="content"
          label="Содержание урока"
        >
          <MarkdownEditor
            value={form.getFieldValue('content') || ''}
            onChange={(val: string | undefined) => form.setFieldValue('content', val)}
          />
        </Form.Item>

        <Form.Item
          name="sort_order"
          label="Порядковый номер"
          rules={[{ required: true, message: 'Пожалуйста, введите порядковый номер' }]}
        >
          <InputNumber 
            min={1} 
            placeholder="1" 
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="examType"
          label="Тип экзамена"
          rules={[{ required: true, message: 'Пожалуйста, выберите тип экзамена' }]}
        >
          <Select placeholder="Выберите тип экзамена">
            <Option value="test">Тест</Option>
            <Option value="open">Открытый вопрос</Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Отмена
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={isEditing ? <SaveOutlined /> : <PlusOutlined />}
            >
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default LessonForm; 